"use strict"

/**
 *	----------------------------------------------------------------------------------------------
 *	Imports
 * 	----------------------------------------------------------------------------------------------
 */
import 'promise-polyfill/src/polyfill';

import {eventBus} from '../event/AdmEventBus';

/**
 *	----------------------------------------------------------------------------------------------
 *	Class Main-Application
 * 	----------------------------------------------------------------------------------------------
 */
class AdmApp{
	
    /**
     * Create a basic adamantium app.
     * @param {Object} config - Configuration object of adamantium app
     */
	constructor(config){
		window.app = this;
		//	Public Properties
		this.darkMode = false;
		this.theme = '';
		this.slimjs = false;
		//	Private Properties		
		this._modules = {};
		this._components = {};
		this._processList = [];

		if(config?.slimjs){ this.slimjs = config.slimjs; }
		if(config?.theme){ this.setTheme(config.theme); }
		if(config?.darkMode){ this.setDarkMode(config.darkMode); }

		eventBus.addEventListener("navigate", (event) => {
			if(this.router){
				this.router.navigate(event.detail.path, event.detail.data, '', event.detail.replace);
			}
		})
		eventBus.addEventListener("changeLanguage", (event) => {
			if(this.i18n){
				this.i18n.setLanguage(event.detail.language);
				this.reload();
			}
		})
		eventBus.addEventListener("error", (event) => {
			console.log(event.detail);
		})		
	}

	/**
     * Initialize app
     */
	 async init(){	
		if( this.i18n ){
			if( this.i18n.api ){
				const textData = await this.i18n.getText().catch(err => { eventBus.dispatchEvent("error", err) });
				if( this?.slimjs?.textPlugin ){
					this.slimjs.textPlugin.add(textData);
				}
				else{
					eventBus.dispatchEvent("textReady", textData);
				}
			}

			if( this.router ){
				this.i18n.languages.forEach((lang) => {
					this.router.add(`/${lang}(/*after)`, null, (routeData) => {
						this.i18n.setLanguage(lang);
						const path = `/${routeData.pathParams.after}`;
						window.history.replaceState(null, null, path)
						this.router.navigate(path);
					})
				});
			}
		} 

		if( this.pager ){
			this.pager.addEventListener("pageReady", () => {
				if(this.prerender){
					setTimeout(() => {
						this.prerender.setPrerenderReady(true);
					}, 2000)
				}
			});

			if(this.pager.defaultLayout){
				await this.pager.setLayout(this.pager.defaultLayout);
			}
		}

		if( this.router ) { 
			this.router.addEventListener("routeChanged", (evt) => {
				if(this.prerender?.checkPrerender()){
					this.prerender.setPrerenderStatusCode(200);
				}
				eventBus.dispatchEvent("changePage", evt.detail);
			})
			this.router.addEventListener("routeFailed", (evt) => {
				if(this.prerender?.checkPrerender()){
					this.prerender.setPrerenderStatusCode(404);
				}
				if(this.pager && this.page404){
					evt.detail.config = Object.assign({page: this.router.path404}, evt.detail.config);
					eventBus.dispatchEvent("changePage", evt.detail);
				}
			})
			this.router.navigate(location.pathname + location.search + location.hash);
		 }
	}
	
	/**
     * Reload app
     */
	reload(){
		top.location = location.pathname + location.search + location.hash;
	}

	/**
     * Set app theme.
	 * @param {Boolean} value
     */
	setTheme (value){
		document.body.classList.remove(this.theme);
		document.body.classList.add(value);
		this.theme = value;
	}

	/**
     * Set app darkmode setting.
	 * @param {Boolean} value
     */
	setDarkMode (value){
		this.darkMode = value;
		if(value === true){
			document.body.setAttribute("data-adm-mode", "dark");
		}
		else{
			document.body.setAttribute("data-adm-mode", "");
		}
	}

	/**
     * Add a adamantium module.
	 * @param {string} name - Module key name
     * @param {Class} Module - Class of an adamantium module
	 * @param {Object} config - Configuration object of a module 
	 * @return {Promise} A resolved promise
     */
	module(name, Module, config){
		const instance = new Module(config);
		Object.defineProperty(this, name, {
			get: () => { return this._modules[name]; },
			set: (module) => { return this._modules[name] = module; }
		}); 
		this[name] = instance;
		return Promise.resolve(this[name]);
	}

	/**
     * Add a adamantium component.
	 * @param {string} name - Componentt key name
     * @param {Class} Component - Class of an adamantium component
	 * @param {Object} config - Configuration object of a component 
	 * @return {Promise} A resolved promise
     */
	component(name, Component, config){
		const instance = new Component(config);
		Object.defineProperty(this, name, {
			get: () => { return this._components[name]; },
			set: (component) => { return this._components[name] = component; }
		}); 
		this[name] = instance;
		return Promise.resolve(this[name]);
	}
}

export { AdmApp }